import './activity.scss';
import React, { useEffect, useState } from 'react';
import { VirtualizedTable } from '../reports-and-logs/virtualized-table/virtualized-table';
import { useStores } from '../../store';
import { activityColumns } from './activity-column-config';
import { Search } from '../reports-and-logs/logs/search/search';
import { observer } from 'mobx-react-lite';
import { useActivityCriteriaConfig } from './activity-criteria-config';
import { ExportActivityLogs } from './export-activity-logs';
import { EmptyState } from '../../shared/empty-state/empty-state';
import { IconType } from '../../shared/custom-icon/custom-icon';
import { ActivityDto } from '@src/dtos/activity.dto';
import { InsightsSubMenu, MainPages, SortDirection } from '@src/utils/enums';
import { Input } from 'antd';
import SearchIcon from '@svg/search.svg?react';
import CustomButton from '../../shared/custom-button/custom-button';
import LogsSearch from '@svg/logs-search.svg?react';
import { usePageViewEvent } from '@src/components/shared/hooks/use-page-view-event';

export const ActivityPage: React.FC = observer(() => {

    const {activityStore, logsStore} = useStores();
    const criteriaConfig = useActivityCriteriaConfig();
    const [tableData, setTableData] = useState<ActivityDto[]>([]);
    const [newSearch, setNewSearch] = useState<number>(0);
    const [searchText, setSearchText] = useState('');
    const [expandSearch, setExpandSearch] = useState(true);

    usePageViewEvent(MainPages.INSIGHTS, InsightsSubMenu.ACTIVITY);

    useEffect(() => {
        logsStore.setActiveSubMenu(InsightsSubMenu.ACTIVITY);
    }, []);

    useEffect(() => {
        void activityStore.load();
    }, []);

    useEffect(() => {
        let searchedData;
        if (searchText) {
            searchedData = activityStore.data?.filter(activity => {
                return Object.values(activity).some(value =>
                    value.toString().toLowerCase().includes(searchText.toLowerCase())
                );
            });
        } else {
            searchedData = [...activityStore.data || []];
        }
        setTableData([...searchedData || []]);
    }, [searchText]);

    const sortData = <K extends keyof ActivityDto>(sortBy: K, sortDirection: SortDirection) => {
        activityStore.setSortDetails(sortBy, sortDirection);
        return activityStore.loadAudit();
    };

    useEffect(() => {
        setTableData(activityStore.data || []);
    }, [activityStore.data]);

    const AuditEmptyState = () => {
        return <EmptyState
            iconType={IconType.NoResult}
            title="No Activity Found"
            subTitle="Change the search criteria and try again."/>;
    };

    return <div className="activity-page">
        <div className="activity-page-header" data-testid="page-header">
            <div className="left-header">
                <div className="total-records">Total: {tableData.length}</div>
                <Input className={'filter-results'} placeholder={'Filter Results'} value={searchText}
                       onChange={(e) => setSearchText(e.target.value)}
                       allowClear prefix={<SearchIcon/>}/>
            </div>
            <div className="right-header">
                <CustomButton label={''} className={`search secondary-btn ${expandSearch && 'active'}`}
                              prefixIcon={<LogsSearch/>} onClick={() => setExpandSearch(!expandSearch)}/>
                <ExportActivityLogs/>
            </div>
        </div>
        <div className="page-content">
            <div>
                <Search show={expandSearch}
                        isLoading={activityStore.isLoading}
                        criteriaConfig={criteriaConfig}
                        setSearchCriteria={(searchCriteria) => activityStore.setSearchCriteria(searchCriteria)}
                        currentSearchCriteria={activityStore.searchCriteria}
                        onSearch={() => {
                            setNewSearch(prev => prev + 1);
                            setSearchText('');
                            return activityStore.load();
                        }}
                        hasPermissions={true}/>
            </div>
            <div className={'activity-table-wrapper'}>
                <VirtualizedTable content={tableData}
                                  isInitialState={!activityStore.data}
                                  columnsConfig={activityColumns}
                                  isLoading={activityStore.isLoading}
                                  noResultIcon={IconType.NoResult}
                                  sortInBe={sortData}
                                  emptyStateComp={<AuditEmptyState/>}
                                  rowCount={tableData.length}
                                  searchText={searchText}
                                  newSearch={newSearch}/>
            </div>
        </div>
    </div>;
});
